import { doesFocusableExist, getCurrentFocusKey, setFocus } from '@noriginmedia/norigin-spatial-navigation';

/** useExtendFocusable 이 DOM 노드에 심어두는 focusKey 표시. DOM 노드로 focusKey 를 역추적할 때 사용한다. */
export const FOCUS_KEY_ATTRIBUTE = 'data-focus-key';

/** 좌측 메뉴 루트 표시. 진입 대상 후보에서 메뉴 자신을 걸러내는 데 사용한다. */
export const MENU_ROOT_ATTRIBUTE = 'data-menu-root';

/** 좌측 메뉴 컨테이너의 focusKey. 줄 왼쪽 끝에서 메뉴로 빠져나갈 때 사용한다. */
export const MENU_FOCUS_KEY = 'MENU';

/** 가로 스크롤 줄 표시. 좌/우 이동 후보를 그 줄 안으로 한정하는 데 사용한다. */
export const FOCUS_ROW_ATTRIBUTE = 'data-focus-row';

// 절반 이상 보여야 "화면에 보이는" 것으로 취급한다.
const VISIBLE_RATIO = 0.5;

// 같은 줄로 묶어서 볼 top 오차 (px).
const ROW_TOLERANCE = 100;

export type FocusScrollAxis = 'horizontal' | 'vertical';

// 포커스 이동에 따른 자동 스크롤을 축별로 억제하기 위한 플래그.
// 목록 컴포넌트들의 onFocus 핸들러는 중간 래퍼를 거치면서 FocusDetails 를 그대로 넘기지 않기 때문에
// details 로 전달하는 대신 동기 플래그를 쓴다. (setFocus → onFocus 는 동기 호출이다)
const suppressedAxes: Record<FocusScrollAxis, boolean> = { horizontal: false, vertical: false };

/** 해당 축의 자동 스크롤을 지금 건너뛰어야 하는지. 스크롤을 만지는 onFocus 핸들러 맨 앞에서 확인한다. */
export const isFocusScrollSuppressed = (axis: FocusScrollAxis) => suppressedAxes[axis];

/** 지정한 축의 자동 스크롤만 억제한 채로 포커스를 옮긴다. */
const setFocusWithoutScroll = (focusKey: string, axes: Array<FocusScrollAxis>) => {
  axes.forEach((axis) => {
    suppressedAxes[axis] = true;
  });

  try {
    setFocus(focusKey);
  } finally {
    axes.forEach((axis) => {
      suppressedAxes[axis] = false;
    });
  }
};

/** 포커스 가능한 말단 요소들. 메뉴 자신과, 다른 포커스 요소를 품은 컨테이너는 제외한다. */
const collectLeafFocusableNodes = (scope: ParentNode = document) => {
  const nodes = Array.from(scope.querySelectorAll<HTMLElement>(`[${FOCUS_KEY_ATTRIBUTE}]`));
  const menu = document.querySelector<HTMLElement>(`[${MENU_ROOT_ATTRIBUTE}]`);
  const candidates = new Set(nodes);
  const containers = new Set<HTMLElement>();

  nodes.forEach((node) => {
    let current = node.parentElement;

    while (current) {
      if (candidates.has(current)) {
        containers.add(current);
        break;
      }

      current = current.parentElement;
    }
  });

  return nodes.filter((node) => !containers.has(node) && !(menu && menu.contains(node)));
};

const isMostlyVisible = (rect: DOMRect) => {
  const visibleWidth = Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0);
  const visibleHeight = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);

  return visibleWidth > rect.width * VISIBLE_RATIO && visibleHeight > rect.height * VISIBLE_RATIO;
};

// 완전히 보이는 항목을 우선 고른다.
// norigin 이 포커스를 줄 때 호출하는 node.focus() 는 요소가 잘려 있으면 브라우저가 알아서 화면 안으로
// 스크롤해 버린다. (2.0.0 에는 이를 끄는 domNodeFocusOptions 가 없다)
// 이미 완전히 보이는 요소면 브라우저도 스크롤하지 않으므로, 진입 시 스크롤이 전혀 움직이지 않는다.
const isFullyVisible = (rect: DOMRect) =>
  rect.top >= 0 && rect.left >= 0 && rect.bottom <= window.innerHeight && rect.right <= window.innerWidth;

/**
 * 화면에 실제로 보이는 포커스 가능한 말단 요소 중, 좌상단에 가장 가까운 것의 focusKey 를 돌려준다.
 *
 * norigin 은 컨테이너에 진입할 때 마지막으로 포커스했던 자식(lastFocusedChildKey)을 복원한다.
 * 그런데 사용자가 손으로 스크롤하면 포커스는 따라오지 않으므로 그 항목이 화면 밖으로 나가고,
 * 그 상태에서 복원하면 스크롤이 예전 위치로 되돌아가 버린다.
 * 그래서 메뉴에서 콘텐츠로 진입할 때는 복원 대신 "지금 보이는" 항목으로 이동시킨다.
 */
const findTopLeftMostFocusKey = (nodes: Array<HTMLElement>, isVisible: (rect: DOMRect) => boolean) => {
  let nearestKey: string | undefined;
  let nearestRow = Number.POSITIVE_INFINITY;
  let nearestLeft = Number.POSITIVE_INFINITY;

  for (let i = 0; i < nodes.length; i += 1) {
    const node = nodes[i];
    const rect = node.getBoundingClientRect();

    if (rect.width === 0 || rect.height === 0 || !isVisible(rect)) continue;

    // 위 → 왼쪽 순으로 가장 가까운 것을 고른다.
    const row = Math.round(rect.top / ROW_TOLERANCE);
    if (row > nearestRow) continue;
    if (row === nearestRow && rect.left >= nearestLeft) continue;

    nearestKey = node.getAttribute(FOCUS_KEY_ATTRIBUTE) ?? undefined;
    nearestRow = row;
    nearestLeft = rect.left;
  }

  return nearestKey;
};

export const findNearestVisibleFocusKey = () => {
  const nodes = collectLeafFocusableNodes();

  // 완전히 보이는 것을 우선하고, 없으면 절반 이상 보이는 것으로 넓힌다.
  return findTopLeftMostFocusKey(nodes, isFullyVisible) ?? findTopLeftMostFocusKey(nodes, isMostlyVisible);
};

const centerX = (rect: DOMRect) => (rect.left + rect.right) / 2;
const centerY = (rect: DOMRect) => (rect.top + rect.bottom) / 2;

// 카드 높이가 섞여 있어도(예: 홈의 콘텐츠 그룹 카드) 같은 줄로 판정되도록 세로 중심으로 비교한다.
const isSameRow = (a: DOMRect, b: DOMRect) => Math.abs(centerY(a) - centerY(b)) * 2 < Math.min(a.height, b.height);

const overlapsHorizontally = (a: DOMRect, b: DOMRect) => a.left < b.right && a.right > b.left;

// 상/하 이동은 가로 스크롤을 하지 않으므로, 도착 카드는 가로로 충분히 보이는 것이어야 한다.
const isMostlyVisibleHorizontally = (rect: DOMRect) =>
  Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0) > rect.width * VISIBLE_RATIO;

/** 좌측 메뉴가 지금 포커스를 받을 수 있는 상태인지. (메뉴가 없거나 숨겨진 화면에서는 내보내지 않는다) */
const isMenuAvailable = () => {
  const menu = document.querySelector<HTMLElement>(`[${MENU_ROOT_ATTRIBUTE}]`);

  return Boolean(menu && menu.offsetParent) && doesFocusableExist(MENU_FOCUS_KEY);
};

/** onArrowPress 는 현재 포커스된 컴포넌트에서만 호출되므로, 현재 focusKey 로 그 노드를 찾는다. */
const getCurrentFocusedNode = () => {
  const currentKey = getCurrentFocusKey();

  return currentKey ? document.querySelector<HTMLElement>(`[${FOCUS_KEY_ATTRIBUTE}="${currentKey}"]`) : null;
};

/**
 * 좌/우 이동을 같은 줄 안으로 제한한다.
 *
 * 줄마다 카드 수가 다르면 좌표만 보는 norigin 기본 탐색이 줄 끝에서 아래 줄로 건너뛴다.
 * (예: 극장판 마지막 카드에서 오른쪽 키 → TV 시리즈 중간 카드)
 * 같은 줄에 대상이 없으면 오른쪽은 막고, 왼쪽은 좌측 메뉴로 내보낸다.
 */
export const focusHorizontalNeighbor = (direction: 'left' | 'right'): boolean => {
  const node = getCurrentFocusedNode();

  if (!node) {
    return true;
  }

  const currentRect = node.getBoundingClientRect();
  const forward = direction === 'right';

  let nearestKey: string | undefined;
  let nearestDistance = Number.POSITIVE_INFINITY;

  // 가로 줄 안에서는 그 줄의 카드만 후보로 본다. (줄 밖 카드는 어차피 같은 줄 판정에서 떨어진다)
  const row = node.closest(`[${FOCUS_ROW_ATTRIBUTE}]`);

  collectLeafFocusableNodes(row ?? document).forEach((candidate) => {
    if (candidate === node) return;

    const rect = candidate.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0 || !isSameRow(rect, currentRect)) return;

    const distance = forward ? rect.left - currentRect.right : currentRect.left - rect.right;
    if (distance < 0 || distance >= nearestDistance) return;

    nearestKey = candidate.getAttribute(FOCUS_KEY_ATTRIBUTE) ?? undefined;
    nearestDistance = distance;
  });

  if (!nearestKey) {
    // 오른쪽은 줄을 벗어나지 않게 막는다.
    if (forward) {
      return false;
    }

    // 왼쪽 끝에서는 좌측 메뉴로 직접 내보낸다.
    if (!isMenuAvailable()) {
      return true;
    }

    setFocus(MENU_FOCUS_KEY);

    return false;
  }

  setFocus(nearestKey);

  return false;
};

/**
 * 위/아래 이동을 "지금 화면에 보이는" 기준으로 처리한다.
 *
 * norigin 은 offsetParent 체인을 따라 좌표를 재는데, 가로 스크롤 컨테이너는 position 이 지정돼 있지 않아
 * 그 체인에 없다. 그래서 컨테이너의 scrollLeft 가 좌표에 반영되지 않고, 줄마다 가로 스크롤 위치가 다르면
 * 화면상 바로 아래가 아니라 "콘텐츠 좌표가 같은" 카드를 고르게 된다.
 * 여기서는 getBoundingClientRect(화면 좌표)로 다시 골라 화면상 바로 아래(위) 카드로 이동시킨다.
 */
export const focusVerticalNeighbor = (direction: 'up' | 'down'): boolean => {
  const node = getCurrentFocusedNode();

  if (!node) {
    return true;
  }

  const currentRect = node.getBoundingClientRect();
  const forward = direction === 'down';

  const candidates = collectLeafFocusableNodes()
    .map((candidate) => ({ candidate, rect: candidate.getBoundingClientRect() }))
    .filter(({ candidate, rect }) => {
      if (candidate === node || rect.width === 0 || rect.height === 0) return false;
      // 같은 줄과, 가로 스크롤로 가려진 카드는 대상이 아니다.
      if (isSameRow(rect, currentRect) || !isMostlyVisibleHorizontally(rect)) return false;

      return forward ? centerY(rect) > centerY(currentRect) : centerY(rect) < centerY(currentRect);
    });

  if (candidates.length === 0) {
    return true;
  }

  // norigin 과 같은 기준으로 가로로 겹치는 후보를 우선한다.
  // (좌측 사이드바처럼 나란히 있지 않은 요소가 끼어드는 것을 막는다)
  const overlapping = candidates.filter(({ rect }) => overlapsHorizontally(rect, currentRect));
  const pool = overlapping.length > 0 ? overlapping : candidates;

  const verticalDistance = (rect: DOMRect) => Math.abs(centerY(rect) - centerY(currentRect));
  const horizontalDistance = (rect: DOMRect) => Math.abs(centerX(rect) - centerX(currentRect));

  // 방향상 가장 가까운 줄을 찾고, 그 줄에서 화면상 x 가 가장 가까운 카드를 고른다.
  const nearestRow = pool.reduce((a, b) => (verticalDistance(b.rect) < verticalDistance(a.rect) ? b : a));
  const target = pool
    .filter(({ rect }) => isSameRow(rect, nearestRow.rect))
    .reduce((a, b) => (horizontalDistance(b.rect) < horizontalDistance(a.rect) ? b : a));

  const targetKey = target.candidate.getAttribute(FOCUS_KEY_ATTRIBUTE);

  if (!targetKey) {
    return true;
  }

  // 새 줄을 화면에 들여야 하니 세로 스크롤은 그대로 두고, 가로 스크롤만 막는다.
  // (가로까지 두면 도착한 카드를 줄 앞쪽으로 당기면서 카드가 옆으로 밀린다)
  setFocusWithoutScroll(targetKey, ['horizontal']);

  return false;
};

/** 카드에서 방향키를 눌렀을 때의 이동 처리. 기본 탐색에 맡길 경우 true 를 돌려준다. */
export const focusNeighborInView = (direction: string): boolean => {
  if (direction === 'left' || direction === 'right') {
    return focusHorizontalNeighbor(direction);
  }

  if (direction === 'up' || direction === 'down') {
    return focusVerticalNeighbor(direction);
  }

  return true;
};

/**
 * 좌측 메뉴에서 우측 키를 눌렀을 때의 진입 처리.
 * 이동시켰으면 false 를 돌려 norigin 의 기본 탐색(= 예전 포커스 복원)을 막는다.
 * 보이는 항목을 못 찾았으면 true 를 돌려 기본 탐색에 맡긴다. (로딩 중 등)
 */
export const focusNearestVisibleContent = () => {
  const focusKey = findNearestVisibleFocusKey();

  if (!focusKey) {
    return true;
  }

  // 이미 화면에 보이는 항목으로 이동하는 것이므로 가로/세로 모두 그대로 둔다.
  setFocusWithoutScroll(focusKey, ['horizontal', 'vertical']);

  return false;
};
